import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, LogOut, Download, ArrowLeft, UtensilsCrossed, Check, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import { orderApi } from "../lib/orderApi";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [products, setProducts] = useState([]);
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [p, t, c] = await Promise.all([
        orderApi.getProducts(),
        orderApi.getTables(),
        orderApi.getCategories(),
      ]);

      setProducts(p || []);
      setTables(t.data || []);
      setCategories(c || []);
    } catch (err) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getQR = (tableId) => {
    const origin = window.location.origin;
    const url = `${origin}/order?table=${tableId}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
  };

  const addTable = async () => {
    const name = prompt("Enter table name (e.g. Table 10)");
    if (!name) return;

    try {
      await orderApi.addTable(name);
      toast.success("Table created successfully");
      fetchData();
    } catch {
      toast.error("Failed to add table");
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto bg-[#FFFDF9] min-h-screen space-y-6">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-stone-200">
        <div className="flex items-center gap-3">
          <Link to="/" className="p-2 text-stone-500 hover:text-stone-900 rounded-full hover:bg-stone-100">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold text-[#2C3E50] flex items-center gap-2">
              <Shield className="w-5 h-5 text-sky-600" />
              <span>Admin Management Dashboard</span>
            </h1>
            <p className="text-xs text-stone-500">
              HimCream Naturals • Products, Tables & Store Operations
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={addTable} className="bg-sky-500 hover:bg-sky-600 text-white font-bold text-xs">
            <Plus className="w-4 h-4 mr-1" />
            <span>Add Table</span>
          </Button>

          <Button
            variant="outline"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="text-xs font-semibold"
          >
            <LogOut className="w-4 h-4 mr-1" />
            <span>Logout</span>
          </Button>
        </div>
      </div>

      {/* PRODUCTS SECTION */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#2C3E50]">Menu Products ({products.length})</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <div key={p.id} className="bg-white p-4 rounded-2xl border border-stone-200/80 shadow-xs flex flex-col justify-between">
              <div className="flex gap-3">
                <img
                  src={p.image_url || "/menu1.jpeg"}
                  alt={p.name}
                  onError={(e) => (e.target.src = "/menu1.jpeg")}
                  className="h-20 w-20 object-cover rounded-xl bg-stone-100 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-sm text-[#2C3E50] truncate">{p.name}</h3>
                  {p.description && (
                    <p className="text-xs text-stone-500 line-clamp-2 mt-0.5">{p.description}</p>
                  )}
                  <p className="font-bold text-sm text-sky-600 mt-1">₹{Number(p.price).toFixed(2)}</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 mt-3 border-t border-stone-100">
                <Badge variant="secondary" className="text-[10px]">
                  {categories.find((c) => c.id === p.category_id)?.name || "Uncategorized"}
                </Badge>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-7 px-2"
                    onClick={async () => {
                      await orderApi.updateProduct(p.id, {
                        ...p,
                        is_available: p.is_available ? 0 : 1,
                      });
                      fetchData();
                    }}
                  >
                    {p.is_available ? "Disable" : "Enable"}
                  </Button>

                  <Button
                    size="sm"
                    variant="destructive"
                    className="text-xs h-7 px-2"
                    onClick={async () => {
                      if (window.confirm(`Delete ${p.name}?`)) {
                        await orderApi.deleteProduct(p.id);
                        fetchData();
                      }
                    }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ADD PRODUCT FORM */}
      <AddProduct categories={categories} onRefresh={fetchData} />

      {/* TABLES SECTION */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-[#2C3E50]">Tables & QR Codes ({tables.length})</h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {tables.map((t) => (
            <div
              key={t.id}
              className="bg-white p-4 rounded-2xl border border-stone-200/80 shadow-xs text-center space-y-2 flex flex-col justify-between"
            >
              <div>
                <img
                  src={getQR(t.id)}
                  alt={`QR for ${t.name}`}
                  className="w-24 h-24 mx-auto object-contain bg-stone-50 p-1 rounded-lg border border-stone-100"
                />
                <p className="font-bold text-sm text-[#2C3E50] mt-2">{t.name}</p>
              </div>

              <div className="flex gap-1.5 justify-center pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-[11px] h-7 px-2 flex-1"
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = getQR(t.id);
                    link.download = `HimCream_${t.name}_QR.png`;
                    link.click();
                  }}
                >
                  <Download className="w-3 h-3 mr-1" />
                  <span>QR</span>
                </Button>

                <Button
                  size="sm"
                  variant="destructive"
                  className="text-[11px] h-7 px-2"
                  onClick={async () => {
                    if (window.confirm(`Delete ${t.name}?`)) {
                      await orderApi.deleteTable(t.id);
                      fetchData();
                    }
                  }}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AddProduct({ categories, onRefresh }) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    category_id: "",
    image_url: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!form.name || !form.price || !form.category_id) {
      toast.error("Please fill in Name, Price, and Category");
      return;
    }

    setSubmitting(true);
    try {
      await orderApi.addProduct({
        ...form,
        price: Number(form.price),
      });

      toast.success("Product added successfully!");
      setForm({
        name: "",
        description: "",
        price: "",
        category_id: "",
        image_url: "",
      });
      onRefresh();
    } catch {
      toast.error("Failed to add product");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-5 rounded-2xl border border-stone-200/80 shadow-xs space-y-3">
      <h3 className="font-bold text-base text-[#2C3E50]">Add New Menu Item</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          placeholder="Product Name (e.g. Alphonso Mango Scoop)"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />

        <Input
          placeholder="Price in INR (e.g. 60)"
          type="number"
          value={form.price}
          onChange={(e) => setForm({ ...form, price: e.target.value })}
        />

        <Input
          placeholder="Short Description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />

        <Input
          placeholder="Image URL (Unsplash or direct image link)"
          value={form.image_url}
          onChange={(e) => setForm({ ...form, image_url: e.target.value })}
        />

        <select
          value={form.category_id}
          onChange={(e) => setForm({ ...form, category_id: e.target.value })}
          className="w-full bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-xs sm:text-sm sm:col-span-2"
        >
          <option value="">Select Category</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full bg-sky-500 hover:bg-sky-600 text-white font-bold text-xs sm:text-sm py-2.5"
      >
        {submitting ? "Adding..." : "+ Create Product"}
      </Button>
    </div>
  );
}
